import api from './api';
import { areRuntimeMocksEnabled } from './runtimeMocks';

export interface Promotion {
  id: number;
  name: string;
  description: string;
  type: 'percentage' | 'fixed_amount' | 'fixed' | 'free_service' | 'bundle' | 'FGA1' | 'FGA2' | '3splits_50%' | 'fga1' | 'fga2' | 'fga3' | 'annual_semestriel' | 'annuel_semestriel' | string;
  value: number; // Pourcentage ou montant fixe
  code: string;
  status: 'active' | 'expired' | 'upcoming' | 'cancelled';
  target: 'all' | 'customers' | 'technicians' | 'new_users';
  start_date: string;
  end_date: string;
  usage_limit?: number;
  used_count: number;
  created_at: string;
  updated_at: string;
  conditions?: {
    min_order_amount?: number;
    max_discount_amount?: number;
    applicable_services?: string[];
    first_time_only?: boolean;
  };
}

export interface PromotionFilters {
  status?: string;
  type?: string;
  target?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PromotionsResponse {
  success: boolean;
  data: {
    promotions: Promotion[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    stats?: {
      total: number;
      active: number;
      upcoming: number;
      expired: number;
    };
  };
}

// Données mockées pour les promotions
const mockPromotions: Promotion[] = [
  {
    id: 1,
    name: "Réduction été 2024",
    description: "Profitez de 20% de réduction sur tous nos services de climatisation",
    type: 'percentage',
    value: 20,
    code: "ETE2024",
    status: 'active',
    target: 'all',
    start_date: '2024-06-01T00:00:00Z',
    end_date: '2024-08-31T23:59:59Z',
    usage_limit: 100,
    used_count: 25,
    created_at: '2024-05-15T10:00:00Z',
    updated_at: '2024-06-01T08:00:00Z',
    conditions: {
      min_order_amount: 150,
      max_discount_amount: 200,
      applicable_services: ['climatisation', 'maintenance'],
      first_time_only: false
    }
  },
  {
    id: 2,
    name: "Service gratuit nouveaux clients",
    description: "Premier diagnostic gratuit pour tous les nouveaux clients",
    type: 'free_service',
    value: 0,
    code: "NEWCLIENT2024",
    status: 'active',
    target: 'new_users',
    start_date: '2024-01-01T00:00:00Z',
    end_date: '2024-12-31T23:59:59Z',
    usage_limit: 500,
    used_count: 78,
    created_at: '2023-12-15T10:00:00Z',
    updated_at: '2024-01-01T08:00:00Z',
    conditions: {
      first_time_only: true,
      applicable_services: ['diagnostic']
    }
  },
  {
    id: 3,
    name: "Pack maintenance annuel",
    description: "Économisez 15% sur notre pack maintenance annuel",
    type: 'percentage',
    value: 15,
    code: "PACK2024",
    status: 'active',
    target: 'customers',
    start_date: '2024-03-01T00:00:00Z',
    end_date: '2024-05-31T23:59:59Z',
    usage_limit: 50,
    used_count: 32,
    created_at: '2024-02-15T10:00:00Z',
    updated_at: '2024-03-01T08:00:00Z',
    conditions: {
      min_order_amount: 300,
      applicable_services: ['maintenance'],
      first_time_only: false
    }
  },
  {
    id: 4,
    name: "Offre technicien",
    description: "Réduction de 50€ sur les formations pour nos techniciens partenaires",
    type: 'fixed_amount',
    value: 50,
    code: "TECH2024",
    status: 'upcoming',
    target: 'technicians',
    start_date: '2024-09-01T00:00:00Z',
    end_date: '2024-11-30T23:59:59Z',
    usage_limit: 20,
    used_count: 0,
    created_at: '2024-07-15T10:00:00Z',
    updated_at: '2024-07-15T10:00:00Z',
    conditions: {
      min_order_amount: 100,
      applicable_services: ['formation']
    }
  },
  {
    id: 5,
    name: "Black Friday 2023",
    description: "Méga promotion de fin d'année - 30% sur tout",
    type: 'percentage',
    value: 30,
    code: "BLACKFRIDAY2023",
    status: 'expired',
    target: 'all',
    start_date: '2023-11-24T00:00:00Z',
    end_date: '2023-11-27T23:59:59Z',
    usage_limit: 200,
    used_count: 187,
    created_at: '2023-11-01T10:00:00Z',
    updated_at: '2023-11-27T23:59:59Z',
    conditions: {
      min_order_amount: 100,
      max_discount_amount: 500
    }
  }
];

// Fonction pour simuler un délai réseau
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface PromotionsResponse {
  success: boolean;
  data: {
    promotions: Promotion[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    stats?: {
      total: number;
      active: number;
      upcoming: number;
      expired: number;
    };
  };
}

class PromotionsService {
  // Récupérer la liste des promotions
  async getPromotions(filters: PromotionFilters = {}): Promise<PromotionsResponse> {
    try {
      // Essayer d'utiliser l'API réelle en premier
      const response = await api.get<PromotionsResponse>('/promotions', { params: filters });
      console.log('Récupération des promotions depuis l\'API');
      return response.data;
    } catch (error: any) {
      if (!areRuntimeMocksEnabled()) {
        throw error;
      }

      console.warn('API indisponible : mode mock explicite activé pour la lecture des promotions');
      
      // Utiliser les données mockées si l'API n'est pas disponible
      await delay(500);
      
      let filteredPromotions = [...mockPromotions];
      
      // Appliquer les filtres
      if (filters.status) {
        filteredPromotions = filteredPromotions.filter(p => p.status === filters.status);
      }
      
      if (filters.type) {
        filteredPromotions = filteredPromotions.filter(p => p.type === filters.type);
      }
      
      if (filters.target) {
        filteredPromotions = filteredPromotions.filter(p => p.target === filters.target);
      }
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredPromotions = filteredPromotions.filter(p => 
          p.name.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower) ||
          p.code.toLowerCase().includes(searchLower)
        );
      }
      
      // Pagination
      const page = filters.page || 1;
      const limit = filters.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedPromotions = filteredPromotions.slice(startIndex, endIndex);
      
      console.log('Récupération des promotions (données mockées)');
      return {
        success: true,
        data: {
          promotions: paginatedPromotions,
          total: filteredPromotions.length,
          page,
          limit,
          totalPages: Math.ceil(filteredPromotions.length / limit)
        }
      };
    }
  }

  // Récupérer une promotion par son ID
  async getPromotionById(id: number): Promise<Promotion> {
    try {
      const response = await api.get<{ success: boolean; data: Promotion }>(`/promotions/${id}`);
      return response.data.data;
    } catch (error) {
      if (!areRuntimeMocksEnabled()) {
        throw error;
      }

      console.warn('API indisponible : mode mock explicite activé pour la lecture de la promotion');
      
      await delay(300);
      const promotion = mockPromotions.find(p => p.id === id);
      if (!promotion) {
        throw new Error(`Promotion avec l'ID ${id} non trouvée`);
      }
      
      console.log(`Récupération de la promotion ${id} (données mockées)`);
      return { ...promotion };
    }
  }

  // Créer une nouvelle promotion
  async createPromotion(promotionData: Partial<Promotion>): Promise<Promotion> {
    const response = await api.post<{ success: boolean; data: Promotion }>('/promotions', promotionData);
    return response.data.data;
  }

  // Mettre à jour une promotion
  async updatePromotion(id: number, promotionData: Partial<Promotion>): Promise<Promotion> {
    const response = await api.put<{ success: boolean; data: Promotion }>(`/promotions/${id}`, promotionData);
    return response.data.data;
  }

  // Supprimer une promotion
  async deletePromotion(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/promotions/${id}`);
    return response.data;
  }

  // Activer/Désactiver une promotion
  async togglePromotionStatus(id: number, status: 'active' | 'cancelled'): Promise<Promotion> {
    const action = status === 'active' ? 'activate' : 'deactivate';
    const response = await api.post<{ success: boolean; data: Promotion }>(
      `/promotions/${id}/${action}`
    );
    return response.data.data;
  }
}

export const promotionsService = new PromotionsService();
